/**
 * Dialog for setting or updating the due date on an active loan.
 * Owner-only. Uses an HTML date input for simplicity.
 */
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useSetLoanDueDate } from '@/hooks/useLoans';
import { toast } from 'sonner';
import { T, RADIUS, SHADOW } from '@/lib/tokens';

interface SetDueDateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  loanId: string;
  itemName: string;
  currentDueAt?: string | null;
}

/**
 *
 * @param root0
 * @param root0.open
 * @param root0.onOpenChange
 * @param root0.loanId
 * @param root0.itemName
 * @param root0.currentDueAt
 */
export function SetDueDateDialog({
  open,
  onOpenChange,
  loanId,
  itemName,
  currentDueAt,
}: SetDueDateDialogProps) {
  const setDueDate = useSetLoanDueDate();

  const defaultDate = currentDueAt
    ? new Date(currentDueAt).toISOString().split('T')[0]
    : (() => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        return d.toISOString().split('T')[0];
      })();

  const [selectedDate, setSelectedDate] = useState(defaultDate);

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setSelectedDate(
        currentDueAt
          ? new Date(currentDueAt).toISOString().split('T')[0]
          : (() => {
              const d = new Date();
              d.setDate(d.getDate() + 1);
              return d.toISOString().split('T')[0];
            })(),
      );
    }
    onOpenChange(isOpen);
  };

  const handleSubmit = async () => {
    if (!selectedDate) return;

    const dueAt = new Date(selectedDate + 'T23:59:59').toISOString();

    try {
      await setDueDate.mutateAsync({ loanId, dueAt });
      toast.success('Due date updated.');
      onOpenChange(false);
    } catch {
      // Error toast handled by useSetLoanDueDate onError
    }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{currentDueAt ? 'Update Due Date' : 'Set Due Date'}</DialogTitle>
          <DialogDescription>
            {currentDueAt ? 'Change' : 'Set'} the return date for "{itemName}". The borrower will be
            notified.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="due-date">Due date</Label>
            <Input
              id="due-date"
              type="date"
              min={today}
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={() => onOpenChange(false)}
            style={{
              padding: '10px 18px',
              borderRadius: RADIUS.md,
              border: `1px solid ${T.border}`,
              background: T.surfaceSubtle,
              color: T.textSec,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedDate || setDueDate.isPending}
            style={{
              padding: '10px 18px',
              borderRadius: RADIUS.md,
              border: 'none',
              background: T.accent,
              color: T.white,
              fontSize: 14,
              fontWeight: 600,
              cursor: !selectedDate || setDueDate.isPending ? 'default' : 'pointer',
              fontFamily: 'inherit',
              opacity: !selectedDate || setDueDate.isPending ? 0.7 : 1,
              boxShadow: SHADOW.btn.accent,
            }}
          >
            {setDueDate.isPending ? 'Saving...' : currentDueAt ? 'Update Date' : 'Set Date'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
